
import { useState } from 'react';
import apiClient from '../../lib/api';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ForgotPasswordModal = ({ isOpen, onClose }: ForgotPasswordModalProps) => {
  const [formData, setFormData] = useState({
    currentEmail: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen) return null;

  const handleClose = () => {
    onClose();
    setFormData({ currentEmail: '' });
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await apiClient.forgotPassword(formData.currentEmail);
      setSuccessMessage('A secure reset link has been sent to your email. Please check your inbox.');

      setTimeout(() => {
        handleClose();
      }, 5000);
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to send reset request. Please check your email and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <i className="ri-lock-password-line text-primary-500 text-xl"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Reset Password</h2>
                <p className="text-sm text-gray-600">
                  Admin Account
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
              <div className="flex items-center space-x-2">
                <i className="ri-check-line"></i>
                <span className="text-sm">{successMessage}</span>
              </div>
            </div>
          )}

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              <div className="flex items-center space-x-2">
                <i className="ri-error-warning-line"></i>
                <span className="text-sm">{errorMessage}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Account Email</label>
              <input
                type="email"
                value={formData.currentEmail}
                onChange={(e) => setFormData({ currentEmail: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent text-sm"
                placeholder="Enter your registered email"
                required
              />
            </div>

            <div className="bg-primary-50 border border-primary-100 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <i className="ri-information-line text-primary-500 text-lg mt-0.5"></i>
                <div className="text-sm text-gray-700">
                  <p className="font-semibold mb-1">How it works:</p>
                  <ul className="space-y-1 text-xs">
                    <li>• We'll send a secure link to your email</li>
                    <li>• Click the link to set a new password</li>
                    <li>• Your new password will be active immediately</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-lg font-semibold transition-all duration-300 cursor-pointer"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-primary-400 hover:bg-primary-500 text-black py-3 rounded-lg font-semibold transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <i className="ri-loader-line animate-spin"></i>
                    <span>Sending...</span>
                  </div>
                ) : (
                  'Send Reset Request'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
